import { Button } from '@synoptic/ui-kit/button/button.tsx';
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from '@synoptic/ui-kit/drawer/drawer.js';
import { DrawerTitle } from '@synoptic/ui-kit/drawer/drawer.tsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@synoptic/ui-kit/popover/popover.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { space } from '@synoptic/ui-kit/tokens.js';
import { FC, useState } from 'react';
import {
  CustomTargetsDataFragmentFragment,
  PackageInStreamFragmentFragment,
  StripePriceFragmentFragment,
} from '@/__generated__/graphql';
import { PaymentOptionsContent } from '@/components/streams/payment-options/payment-options.tsx';
import { ChoosePackageModal } from '@/components/streams/toggle-paid-stream-subscription/stream-included-in-package-modal.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { followStreamButton } from '@/routes/_app.p.streams_.$id/stream-banner/stream-banner.css.ts';
import { useMe } from '@/routes/_app/use-me.ts';
import {
  paymentOptionsDrawerContent,
  paymentOptionsDrawerTitle,
  paymentOptionsPopoverContent,
} from '@/components/streams/payment-options/payment-options.css';

interface SubscribePaidStreamButtonProps {
  streamId: string;
  packages?: PackageInStreamFragmentFragment[];
  loading?: boolean;
  popoverAlign?: 'center' | 'start' | 'end';
  prices?: StripePriceFragmentFragment[];
  customTargets?: CustomTargetsDataFragmentFragment;
}

export const SubscribePaidStreamButton: FC<SubscribePaidStreamButtonProps> = ({
  streamId,
  loading,
  packages,
  popoverAlign,
  prices,
  customTargets,
}) => {
  const isTablet = useIsTablet();
  const me = useMe();

  const signupCoupon = me.protectedFields?.coupons?.find(
    (c) => c.streamId === streamId && !c.usedAt,
  );

  const [open, setOpen] = useState(false);

  if (loading && open) {
    setOpen(false);
  }

  const onStreamUnlockOpen = () => {
    window.mixpanel?.track('Paid Stream Unlock Opened', {
      'Stream ID': streamId,
    });
  };

  const onUnlockOpenChange = (v: boolean) => {
    setOpen(v);

    if (v) {
      onStreamUnlockOpen();
    }
  };

  const trigger = (
    <Button
      size={isTablet ? 'medium' : 'small'}
      className={followStreamButton}
      loading={loading}
    >
      Unlock
    </Button>
  );

  const onSubmit = () => {
    window.mixpanel?.track('Paid Stream Unlock Initiated', {
      'Stream ID': streamId,
    });
  };

  if (packages && packages.length) {
    return (
      <ChoosePackageModal
        trigger={trigger}
        packages={packages}
        streamId={streamId}
        prices={prices}
        title={'Subscribe to unlock stream'}
        actionTitle={'Buy stream only'}
        open={open}
        onOpenChange={setOpen}
        onStreamUnlockOpen={onStreamUnlockOpen}
        onStreamUnlockSubmit={onSubmit}
      />
    );
  }

  return isTablet ? (
    <Popover open={open} onOpenChange={onUnlockOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className={paymentOptionsPopoverContent}
        side="bottom"
        sideOffset={space.s12}
        align={popoverAlign || 'end'}
      >
        <PaymentOptionsContent
          action={paths.makeStreamUnlock(streamId)}
          onSubmit={onSubmit}
          prices={prices}
          customTargets={customTargets}
          couponId={signupCoupon?.couponId}
          discountAmountCents={signupCoupon?.discountAmountCents}
        />
      </PopoverContent>
    </Popover>
  ) : (
    <Drawer open={open} onOpenChange={onUnlockOpenChange}>
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent
        className={paymentOptionsDrawerContent}
        aria-describedby={undefined}
      >
        <DrawerTitle className={paymentOptionsDrawerTitle}>
          Unlock stream
        </DrawerTitle>
        <PaymentOptionsContent
          action={paths.makeStreamUnlock(streamId)}
          onSubmit={onSubmit}
          prices={prices}
          customTargets={customTargets}
          couponId={signupCoupon?.couponId}
          discountAmountCents={signupCoupon?.discountAmountCents}
        />
      </DrawerContent>
    </Drawer>
  );
};
